import * as React from 'react';
import { useContext, useState, useEffect } from 'react';
import { Button, TextField, Box } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { AuthContext } from '../store/authContext';

type FormInputs = {
  username: string;
  password: string;
};

const LoginForm: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const { login, isLoggedIn } = useContext(AuthContext);
  const history = useNavigate();

  const { register, handleSubmit, formState: { errors } } = useForm<FormInputs>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const onSubmit = async (data: FormInputs) => {
    setLoading(true);
    await login(data.username, data.password);
    setLoading(false);
  };

  useEffect(() => {
    if (isLoggedIn && !loading) {
      history('/');
    }
  }, [history, isLoggedIn, loading]);

  const requiredMessage = t('validation:required');
  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onSubmit)}
      sx={(theme) => ({
        width: '100%',
        marginTop: theme.spacing(1),
        textAlign: 'center',
      })}
    >
      <TextField
        {...register('username', {
          required: requiredMessage,
        })}
        variant="outlined"
        color="secondary"
        margin="normal"
        fullWidth
        id="username"
        label={t('username')}
        autoComplete="username"
        autoFocus
        error={!!errors.username}
        helperText={errors.username?.message}
      />
      <TextField
        {...register('password', { required: requiredMessage })}
        variant="outlined"
        color="secondary"
        margin="normal"
        fullWidth
        label={t('password')}
        type="password"
        id="password"
        autoComplete="current-password"
        error={!!errors.password}
        helperText={errors.password?.message}
      />
      <Button
        type="submit"
        variant="contained"
        color="primary"
        disabled={loading}
        sx={(theme) => ({
          marginTop: theme.spacing(4),
          width: '80%',
          display: 'flex',
          marginLeft: 'auto',
          marginRight: 'auto',
        })}
      >
        {t('login')}
      </Button>
    </Box>
  );
};

export default LoginForm;
